// MUS-448 - Whole-app hard blocker for mobile-web + tablet-web.
//
// The playing experience requires MIDI input. WebKit (Safari, and therefore
// every browser on iOS and iPad) ships no Web MIDI API, and in production the
// only input mode is MIDI (audio recognition is force-disabled), so play cannot
// function on any non-desktop web surface. Rather than drop users onto a broken
// play screen, we hard-block all phone/tablet web and funnel them to the native
// apps. Desktop web (Chrome/Edge) and the native Capacitor apps are unaffected.
//
// This module holds the *pure* decision (no React/DOM/Capacitor) so it can be
// unit-tested deterministically; the component consumes it. This mirrors the
// MUS-427 GetAppBanner logic split and guards against the blocker being silently
// disabled again (its predecessor regressed to a hard-coded `false`).

import { ALLOWED_PATHS_ANY_PLATFORM } from 'Utils/Constants';
import type { DeviceKind } from 'Utils/Constants';
import type { DeviceClass } from 'Utils/Responsive';

// Native app store listings (bundle id com.museflow.app). The iOS URL matches the
// one already used elsewhere in the app; the Android URL matches MUS-427.
export const IOS_APP_STORE_URL = 'https://apps.apple.com/us/app/museflow/id6743666055';
export const ANDROID_PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.museflow.app';

// Auth / onboarding entry points must stay reachable on every surface so a
// phone/tablet-web visitor can still sign in, verify their email (verification
// links routinely open on a phone), or complete an OAuth redirect before being
// funnelled to the app. Matched by prefix so sub-routes (e.g. /verify/abc) count.
export const AUTH_AND_ONBOARDING_PATHS = ['/signin', '/register', '/welcome', '/verify'];

export const isAuthOrOnboardingPath = (pathname: string): boolean =>
  AUTH_AND_ONBOARDING_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));

/**
 * Paths never covered by the mobile/tablet-web blocker: the auth/onboarding entry
 * points above plus the landing/allowed paths shared with the other compatibility
 * blockers (`ALLOWED_PATHS_ANY_PLATFORM`). Everything else is "the app".
 */
export const isBlockerExemptPath = (pathname: string): boolean =>
  isAuthOrOnboardingPath(pathname) || ALLOWED_PATHS_ANY_PLATFORM.includes(pathname);

export type StoreTarget = 'ios' | 'android';

/**
 * Which single app-store button the blocker should show for a device: iOS App
 * Store for iPhone/iPad, Google Play for Android. Returns null for anything else
 * (desktop/web/unknown) - the blocker never shows on those, but a null target is
 * treated as "show both" defensively so a user is never left without a link.
 */
export function storeTargetForDevice(device: DeviceKind): StoreTarget | null {
  if (device === 'iphone' || device === 'ipad') return 'ios';
  if (device === 'android') return 'android';
  return null;
}

/** The store listing URL for a given target, or null (no target -> caller shows both). */
export function storeUrlForTarget(target: StoreTarget | null): string | null {
  if (target === 'ios') return IOS_APP_STORE_URL;
  if (target === 'android') return ANDROID_PLAY_STORE_URL;
  return null;
}

// MUS-427 fold-in: "open the app if it's installed, otherwise the store."
// The native app registers the museflow:// scheme (see App.tsx museflow://signin /
// museflow://auth-callback and the Verification "Continue in App" hand-off), so a
// tap first attempts museflow:// - if the app is installed the OS switches to it and
// this browser tab goes hidden. If, after a short delay, the tab is still visible
// (the scheme was not handled -> app not installed), we send the user to the store.
export const APP_DEEP_LINK_URL = 'museflow://';
export const DEEP_LINK_STORE_FALLBACK_MS = 1500;

export interface OpenAppDeps {
  /** Navigate the current tab to a URL (real impl: window.location.href = url). */
  navigate: (url: string) => void;
  /** Whether this browser tab is still visible (real impl: () => !document.hidden). */
  isVisible: () => boolean;
  /** Schedule the fallback check (real impl: window.setTimeout). */
  schedule: (fn: () => void, ms: number) => void;
  /** Override the fallback delay (defaults to DEEP_LINK_STORE_FALLBACK_MS). */
  delayMs?: number;
}

/**
 * Attempt to open the native app via the museflow:// deep link, falling back to the
 * store listing if the app does not take over. Pure control flow with injected deps
 * so it is unit-testable without a DOM. If storeUrl is null, only the app is attempted
 * (no fallback target to send them to).
 */
export function openAppThenStore(storeUrl: string | null, deps: OpenAppDeps): void {
  deps.navigate(APP_DEEP_LINK_URL);
  if (!storeUrl) return;
  deps.schedule(() => {
    // Still here after the delay means the app did not open; go to the store.
    if (deps.isVisible()) deps.navigate(storeUrl);
  }, deps.delayMs ?? DEEP_LINK_STORE_FALLBACK_MS);
}

export interface MobileWebBlockerInput {
  /** From getDeviceClass() - 'phone' | 'tablet' | 'desktop' | 'unknown'. */
  deviceClass: DeviceClass;
  /** From isCapacitorNative() - true inside the iOS/Android app. */
  isNative: boolean;
  /** The current route pathname. */
  pathname: string;
}

/**
 * The single visibility predicate for the whole-app mobile/tablet-web blocker.
 * Show it when the user is on phone- or tablet-class web (not the native app,
 * not desktop web) and is not on an exempt auth/onboarding/landing path.
 */
export function shouldShowMobileWebBlocker({
  deviceClass,
  isNative,
  pathname,
}: MobileWebBlockerInput): boolean {
  if (isNative) return false; // native app: play works via the CoreMIDI/MidiManager bridge
  if (deviceClass !== 'phone' && deviceClass !== 'tablet') return false; // desktop web: play works
  if (isBlockerExemptPath(pathname)) return false; // keep sign-in / verify / landing reachable
  return true;
}
